import { supabase } from '@/lib/supabase'

const API_URL = import.meta.env.VITE_API_URL as string

if (!API_URL) {
  throw new Error('Missing VITE_API_URL environment variable.')
}

// Auto-detect dev mode based on API URL
// SECURITY: Dev mode ONLY allowed with localhost backends
const isLocalhost = API_URL.includes('localhost') || API_URL.includes('127.0.0.1')
const userRequestedDevMode = import.meta.env.VITE_DEV_MODE === 'true'

if (userRequestedDevMode && !isLocalhost) {
  throw new Error(
    'SECURITY ERROR: Dev mode is not allowed with non-localhost backends.\n' +
    `Current API_URL: ${API_URL}\n` +
    'Dev mode can only be used with localhost or 127.0.0.1 backends.'
  )
}

// Dev mode is only enabled if explicitly requested AND using localhost
const DEV_MODE = userRequestedDevMode && isLocalhost

/**
 * API Error class for typed error handling
 */
export class ApiError extends Error {
  status: number
  statusText: string

  constructor(status: number, statusText: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
  }
}

/**
 * Request options for the API client
 */
interface RequestOptions<TBody = unknown> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: TBody
  headers?: Record<string, string>
  skipAuth?: boolean
}

/**
 * Typed fetch wrapper with automatic auth header injection
 *
 * @param endpoint - API endpoint (will be appended to VITE_API_URL)
 * @param options - Request options
 * @returns Promise with typed response data
 */
export async function apiClient<TResponse, TBody = unknown>(
  endpoint: string,
  options: RequestOptions<TBody> = {}
): Promise<TResponse> {
  const { method = 'GET', body, headers = {}, skipAuth = false } = options

  // Build request headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  }

  // Inject auth header if not skipped (dev mode skips auth entirely)
  if (!skipAuth && !DEV_MODE) {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (session?.access_token) {
      requestHeaders['Authorization'] = `Bearer ${session.access_token}`
    }
  }

  // Build fetch options
  const fetchOptions: RequestInit = {
    method,
    headers: requestHeaders,
  }

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body)
  }

  // Make the request
  const url = `${API_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`
  const response = await fetch(url, fetchOptions)

  // Handle non-OK responses
  if (!response.ok) {
    let errorMessage = response.statusText

    try {
      const errorBody = (await response.json()) as { detail?: string; message?: string }
      errorMessage = errorBody.detail ?? errorBody.message ?? response.statusText
    } catch {
      // Use default status text if JSON parsing fails
    }

    throw new ApiError(response.status, response.statusText, errorMessage)
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as TResponse
  }

  // Parse and return JSON response
  return response.json() as Promise<TResponse>
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: <TResponse>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiClient<TResponse>(endpoint, { ...options, method: 'GET' }),

  post: <TResponse, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'method' | 'body'>
  ) => apiClient<TResponse, TBody>(endpoint, { ...options, method: 'POST', body }),

  put: <TResponse, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'method' | 'body'>
  ) => apiClient<TResponse, TBody>(endpoint, { ...options, method: 'PUT', body }),

  patch: <TResponse, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    options?: Omit<RequestOptions<TBody>, 'method' | 'body'>
  ) => apiClient<TResponse, TBody>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <TResponse>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiClient<TResponse>(endpoint, { ...options, method: 'DELETE' }),
}
